import type { RequestObjective } from "@/hooks/api/use-campaign-requests";

import { REQUEST_OBJECTIVE_LABELS } from "./constants";

/**
 * The last line of defence against an internal value reaching a screen.
 *
 * Every label table in `./constants` is written as `Record<Enum, string>` and read as
 * `TABLE[value] ?? value` — which is correct TypeScript and a lie at runtime. The value crossing
 * the wire is a string the API chose, not the union the file was typed against, so a state, a
 * blocker, a price code or a document kind added on the server (or one this build has simply not
 * heard of) falls straight through the `??` and is printed raw. That is how the admin queue came
 * to carry rows titled `appointment_setting` beside properly named ones.
 *
 * So nothing here invents a label — {@link labelOf} looks one up exactly as before and only
 * changes what happens when the lookup misses: `appointment_setting` reads as "Appointment
 * setting" rather than as itself. A humanised fallback is still a fallback and still means
 * somebody should add the real words; it just stops the gap being the client's problem.
 */

/**
 * The shape every enum on this API takes, and a shape no written label has: all lower case, and
 * at least one underscore or hyphen joining two runs.
 */
const ENUM_VALUE = /^[a-z][\da-z]*(?:[_-][\da-z]+)+$/;

/**
 * `awaiting_confirmation` → "Awaiting confirmation", and everything else through untouched.
 *
 * Leaving non-enum text alone is what makes this safe to wrap around a label function that already
 * falls back to its raw input — `humaniseEnum(agentKindLabel(kind))` fixes the fallback without
 * touching "Same script, translated" — and safe on free text, so a `confirmedByRole` of "Head of
 * Marketing" is not retitled while `account_owner` is.
 */
export function humaniseEnum(value: string | null | undefined): string {
	const text = (value ?? "").trim();
	if (text.length === 0) return "";
	if (!ENUM_VALUE.test(text)) return text;

	const spaced = text.replaceAll(/[_-]+/g, " ");
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * A label table read safely: the written words where there are some, a humanised value where
 * there are not, and one shared em dash where there is no value at all.
 *
 * The tables are `Record<Enum, string>` rather than `Partial<…>`, so the index is typed as always
 * hitting; it is read through `Partial` here precisely because the runtime value may not be in
 * the union the table was declared over.
 */
export function labelOf<T extends string>(
	labels: Record<T, string>,
	value: T | string | null | undefined,
	empty = "—"
): string {
	if (value === null || value === undefined || value === "") return empty;

	return (labels as Partial<Record<string, string>>)[value] ?? humaniseEnum(value);
}

/**
 * A `snake_case` run inside a longer string — the shape every enum on this API takes, and a shape
 * no title anyone typed has. Anchored on word boundaries so `Poly Video · APAC` is left alone.
 */
const ENUM_TOKEN = /\b[a-z][\da-z]*(?:_[\da-z]+)+\b/g;

/**
 * A campaign request's name, as a person would read it.
 *
 * A request created without a name is stored titled after its objective — the raw column value,
 * `appointment_setting` — and the queue, the request page and the preparation header all print
 * whatever is in that column. Rewriting the stored title is the API's decision to make; this is
 * the presentation half, and it deliberately rewrites the enum run wherever it sits rather than
 * only when it is the whole title, because the titles that reach the queue carry prefixes
 * (`[E2E-CLONE] discovery_qualification`) and half a fixed title is not a fix.
 *
 * An objective is translated through its own label — "Discovery & qualification", the words the
 * wizard offered — and anything else falls back to sentence case.
 */
export function requestTitle(title: string | null | undefined, fallback = "Untitled campaign"): string {
	const raw = (title ?? "").trim();
	if (raw.length === 0) return fallback;

	return raw.replaceAll(
		ENUM_TOKEN,
		(token) => REQUEST_OBJECTIVE_LABELS[token as RequestObjective] ?? humaniseEnum(token)
	);
}
